import { QueryStatsOutlined } from "@mui/icons-material";
import {
  alpha,
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useState } from "react";
import { API_SERVICE_BACKEND, DEFAULT_DAYS_ON_CHART } from "src/config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { subDays } from "date-fns";
import DateTimeRangePicker from "./dateTimeRangePicker";
import axios from "axios";
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

export const chartOptions = (theme) => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: 2,
  clip: false,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        boxWidth: 12,
        boxHeight: 12,
        borderRadius: 8,
        color: theme.palette.text.primary,
        font: {
          size: 12,
        },
      },
    },
    title: {
      display: true,
    },
    /* tooltip: {
      mode: "nearest",
      axis: "x",
      intersect: false,
      enabled: (item) => {
        return item?.tooltip?.title?.[0] === "undefined"
          ? false
          : true;
      },
      // filter: (tooltipItem) => {
      //   return !bufferIndices.includes(
      //     tooltipItem.dataIndex
      //   );
      // },
      callbacks: {
        title: (tooltipItem) => {
          const endDateArray =
            tooltipItem?.[0]?.label?.split("/");
          const endDate = `${endDateArray?.[1]}/${endDateArray?.[0]}/${endDateArray?.[2]}`;
          let days = 7; // Days you want to subtract
          let startDate = new Date(endDate);
          let lastWeekDate = new Date(
            startDate?.getTime() -
              days * 24 * 60 * 60 * 1000
          );
          const formattedLastWeekDate =
            moment(lastWeekDate).format("MMM DD"); 

          const formattedEndDate =
            moment(endDate).format("MMM DD");

       
        },
        label: (tooltipItem) => {
          const ref = {
            1: "low",
            2: "medium",
            3: "high",
          };

          return `score : ${
            ref[parseInt(tooltipItem.formattedValue)]
          }`;
        },
      },
    }, */
  },
  scales: {
    x: {
      //stacked: true,
      ticks: {
        color: theme.palette.text.primary,
      },
      grid: {
        color: alpha(theme.palette.text.primary, 0.2),
      },
    },
    y: {
      title: {
        display: true,
        text: "Emails Sent",
        color: theme.palette.text.primary,
        font: {
          weight: "700",
          size: 12,
        },
      },
      ticks: {
        color: theme.palette.text.primary,
      },
      border: {
        display: false,
      },
      grid: {
        color: alpha(theme.palette.text.primary, 0.2),
      },
    },
  },
});

const MailboxStats = ({ acc, isDrawerOpen }) => {
  const theme = useTheme();
  const toUTCString = (date) => date.toISOString();
  const [dateRange, setDateRange] = useState({
    from: subDays(Date.now(), DEFAULT_DAYS_ON_CHART),
    to: new Date(Date.now()),
  });

  const [selectedDateRange, setSelectedDateRange] = useState({
    from: toUTCString(dateRange?.from),
    to: toUTCString(dateRange?.to),
  });
  const onDateRangeChange = (field, value) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value,
    }));
  };
  // const fetchMailboxStats1 = async (signal) => {
  //   try {
  //     const res = await fetch1(
  //       `${API_SERVICE_BACKEND}/getmailboxLogs/${acc?._id}`
  //     );
  //     const userdata = await res.json();
  //     const sortedData = userdata?.data?.logs?.sentHistory || [];
  //     return sortedData;
  //   } catch (error) { }
  // };
  const fetchMailboxStats = async () => {
    try {
      // const res = await fetch1(
      //   `${API_SERVICE_BACKEND}/mailboxes/getMailboxesLogs?senderEmail=${encodeURIComponent(
      //     acc.senderEmail
      //   )}&startDate=${selectedDateRange.from}&endDate=${selectedDateRange.to}`
      // );
      const res = await axios.get(
        `${API_SERVICE_BACKEND}/mailboxes/getMailboxesLogs?senderEmail=${encodeURIComponent(
          acc.senderEmail
        )}&startDate=${selectedDateRange.from}&endDate=${selectedDateRange.to}`
      );
      const userdata = res.data;
      const sortedData =
        userdata[0]?.sentHistory?.sort(
          (a, b) => new Date(a?.calculatedAt) - new Date(b?.calculatedAt)
        ) || [];
      return sortedData;
    } catch (error) {
      console.error("Error fetching logs:", error);
    }
  };

  const {
    data: chartData,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["mailboxStats", acc?._id, selectedDateRange?.from, selectedDateRange?.to],
    queryFn: ({ signal }) => fetchMailboxStats(signal),
    enabled: isDrawerOpen,
    staleTime: Infinity,
    keepPreviousData: true,
  });
  return (
    <Box
      sx={{
        width: "100%",
        px: 2,
        //backgroundColor: "white",
        borderRadius: "12px",
      }}
    >
      <Stack flexDirection={"row"} justifyContent={"flex-end"} alignItems={"center"}>
        <DateTimeRangePicker
          dateRange={dateRange}
          setFormattedDateRange={setSelectedDateRange}
          onDateRangeChange={onDateRangeChange}
        />
      </Stack>
      {isFetching ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
            height: "100%",
            width: "100%",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          {chartData?.length > 0 ? (
            <Stack spacing={1}>
              <Box
                sx={{
                  maxWidth: "70vw",
                  maxHeight: "90vh",
                  minWidth: "60vw",
                  minHeight: "60vh",
                }}
              >
                <Bar
                  options={chartOptions(theme)}
                  data={{
                    labels: chartData?.map((data) =>
                      moment(data?.calculatedAt).format("MMM DD")
                    ),
                    datasets: [
                      {
                        label: "Max Allowed",
                        data: chartData?.map((data) => data?.maxAllowed),
                        backgroundColor: "rgb(255, 162, 0)",
                      },
                      {
                        label: "Emails Sent",
                        data: chartData?.map((data) => data?.emailsSent),
                        backgroundColor: "#5761FE",
                      },
                    ],
                  }}
                />
              </Box>
            </Stack>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 1,
                height: "100%",
                width: "100%",
              }}
            >
              <QueryStatsOutlined sx={{ fontSize: "42px" }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                }}
              >
                No stats found
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default MailboxStats;
